import { useStoreContext } from "./StoreContext";
import type { RootStore } from "./RootStore";

/**
 * Hook to access RootStore and all feature stores
 *
 * This is the primary way to access MobX stores in React components.
 * The hook returns the entire RootStore, allowing destructured access
 * to any feature store.
 *
 * @returns RootStore instance with all feature stores
 * @throws Error if used outside of StoreProvider
 *
 * @example
 * // Access multiple stores
 * function MyComponent() {
 *   const { authStore, listingsStore } = useStore();
 *
 *   return (
 *     <div>
 *       {authStore.user && <p>Welcome {authStore.displayName}</p>}
 *       <p>{listingsStore.listings.length} listings found</p>
 *     </div>
 *   );
 * }
 *
 * @example
 * // Access specific store
 * function AuthButton() {
 *   const { authStore } = useStore();
 *
 *   return (
 *     <button onClick={() => authStore.signOut()}>
 *       Sign Out
 *     </button>
 *   );
 * }
 *
 * @example
 * // Access API through store
 * function DataFetcher() {
 *   const store = useStore();
 *
 *   useEffect(() => {
 *     // Access API via RootStore
 *     store.api.listings.fetchListings();
 *   }, [store]);
 * }
 *
 * @example
 * // Use with observer for reactivity
 * import { observer } from "mobx-react-lite";
 *
 * const UserProfile = observer(() => {
 *   const { authStore } = useStore();
 *
 *   // Will automatically re-render when authStore.user changes
 *   return <div>{authStore.displayName}</div>;
 * });
 */
export function useStore(): RootStore {
  return useStoreContext();
}
